/**
 * #142 · Alta de un pedido de rollos.
 *
 * Los límites son los de `RollRequestIn`: hasta 50.000.000 de etiquetas.
 *
 * **La cantidad va en unidades, no en rollos**, y no es una simplificación de
 * la interfaz: cuántas etiquetas entran en un rollo es un dato de fabricación
 * que XYVONA negocia con el proveedor y que cambia con la tirada. Un cliente
 * que pidiera "tres rollos" estaría pidiendo una cantidad que no conoce.
 *
 * Tampoco hay proveedor ni código de orden de compra: los dos son de la
 * relación XYVONA↔impresor, que es la que este formulario existe para no
 * mezclar. Ver la cabecera de `RollRequestsPage.tsx`.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { useKeysetQuery } from '@/api/hooks';
import { productKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { SelectField, TextAreaField, TextField } from '../products/ui/fields';

type Product = Schemas['ProductOut'];

const MAX_QUANTITY = 50_000_000;
const PRODUCT_OPTIONS = 200;

export interface RollRequestDraft {
  quantity: number;
  product_id: number | null;
  needed_by: string | null;
  notes: string | null;
}

export interface RollRequestFormProps {
  onSubmit: (draft: RollRequestDraft) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  error: unknown;
}

export function RollRequestForm({
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: RollRequestFormProps): ReactNode {
  const { t } = useI18n();
  const [quantity, setQuantity] = useState('100000');
  const [productId, setProductId] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);

  const products = useKeysetQuery<Product>(
    productKeys.list({ limit: PRODUCT_OPTIONS }),
    '/api/v1/products',
    undefined,
    { limit: PRODUCT_OPTIONS },
  );

  const quantityValue = Number(quantity);
  const quantityOk =
    Number.isInteger(quantityValue) && quantityValue >= 1 && quantityValue <= MAX_QUANTITY;
  const quantityError =
    touched && !quantityOk
      ? t('rollRequests.field.quantity.invalid', { max: MAX_QUANTITY })
      : undefined;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setTouched(true);
    if (!quantityOk) return;
    onSubmit({
      quantity: quantityValue,
      product_id: productId === '' ? null : Number(productId),
      needed_by: neededBy === '' ? null : neededBy,
      notes: notes.trim() === '' ? null : notes.trim(),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <TextField
        label={t('rollRequests.field.quantity')}
        hint={t('rollRequests.field.quantity.hint')}
        type="number"
        min={1}
        max={MAX_QUANTITY}
        value={quantity}
        onChange={setQuantity}
        error={quantityError}
        required
      />

      <SelectField
        label={t('rollRequests.field.product')}
        hint={t('rollRequests.field.product.hint')}
        value={productId}
        onChange={setProductId}
        options={[
          { value: '', label: t('rollRequests.field.product.none') },
          ...products.items.map((product) => ({
            value: String(product.id),
            label: `${product.sku} — ${product.name}`,
          })),
        ]}
      />

      <TextField
        label={t('rollRequests.field.neededBy')}
        hint={t('rollRequests.field.neededBy.hint')}
        type="date"
        value={neededBy}
        onChange={setNeededBy}
      />

      <TextAreaField
        label={t('rollRequests.field.notes')}
        hint={t('rollRequests.field.notes.hint')}
        value={notes}
        onChange={setNotes}
        rows={3}
        maxLength={1000}
      />

      {Boolean(error) && <ErrorState error={error} />}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : t('rollRequests.send')}
        </Button>
      </div>
    </form>
  );
}
